'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { t } from '../../../../i18n';
import Icon from '../../../../components/Icon';
import { commentableRange, describeSelection } from '../anchoring';
import { SlideUp } from '../BottomSheet';
import { clamp, visibleBounds } from '../marks';
import {
    useHasRightColumn,
    useReportComments,
} from '../ReportCommentsProvider';

import s from './AddCommentButton.module.scss';

// Between the floating button and the selection.
const GAP = 8;

// Where the selection is in the viewport, and the part of the report around
// it that is in view.
const positionOf = (range) => {
    const box = range.getBoundingClientRect();
    return {
        top: box.top,
        left: box.left + box.width / 2,
        bounds: visibleBounds(range.startContainer),
    };
};

// Above the middle of the selection, but inside the part of the report in
// view. A long selection's start can scroll out of it, and the button would
// go with it, out over the page or off the screen.
const floatingStyle = ({ top, left, bounds }, height) => ({
    top: clamp(top - GAP, bounds.top + height, bounds.bottom),
    left,
});

// Ctrl+Alt+M, or ⌘⌥M on a Mac, as in Google Docs. Option changes the key a Mac
// reports, so the physical key is matched.
const isShortcut = (event) =>
    event.code === 'KeyM' && event.altKey && (event.ctrlKey || event.metaKey);

// Shows while there is a selection that can be commented on. It follows the
// selection rather than the mouse, so keyboard selections get it too.
//
// From 1080px it floats above the selection. Below, it is in a bar at the
// bottom of the screen, clear of the copy and share menu phones show by a
// selection.
const AddCommentButton = () => {
    const { canComment, commentableRef, startDraft, selectionChanged } =
        useReportComments();
    const floats = useHasRightColumn();
    // Where the selection is, while the reader has selected text to comment
    // on.
    const [position, setPosition] = useState(null);
    // The floating button's height, once it has shown. A ref callback sets it
    // before the browser paints, so the button is never drawn out of place.
    const [buttonHeight, setButtonHeight] = useState(0);
    const measureButton = useCallback((button) => {
        if (button) setButtonHeight(button.offsetHeight);
    }, []);

    // Described only now, since that walks the whole report text.
    const addComment = useCallback(() => {
        const described = describeSelection(
            commentableRef.current,
            document.getSelection()
        );
        if (described) startDraft(described);
    }, [commentableRef, startDraft]);

    useEffect(() => {
        if (!canComment) return;
        let range = null;

        const followSelection = () => {
            const selection = document.getSelection();
            const root = commentableRef.current;
            range =
                selection && root ? commentableRange(root, selection) : null;
            setPosition(range && positionOf(range));
            selectionChanged(Boolean(range));
        };

        // The article scrolls in its own container from 1080px, so scrolls
        // are caught on their way down to it.
        const followScroll = () => {
            if (range && floats) setPosition(positionOf(range));
        };

        // Tabbing to the button would pass every link after the selection.
        const followShortcut = (event) => {
            if (!range || !isShortcut(event)) return;
            event.preventDefault();
            addComment();
        };

        followSelection();
        document.addEventListener('selectionchange', followSelection);
        document.addEventListener('keydown', followShortcut);
        window.addEventListener('scroll', followScroll, true);
        window.addEventListener('resize', followScroll);
        return () => {
            document.removeEventListener('selectionchange', followSelection);
            document.removeEventListener('keydown', followShortcut);
            window.removeEventListener('scroll', followScroll, true);
            window.removeEventListener('resize', followScroll);
            selectionChanged(false);
        };
    }, [canComment, floats, commentableRef, addComment, selectionChanged]);

    if (!canComment) return null;

    const button = position && (
        <button
            ref={floats ? measureButton : undefined}
            type="button"
            className={classNames(s.Root, {
                [s['Root--Floating']]: floats,
                [s['Root--Bar']]: !floats,
            })}
            style={floats ? floatingStyle(position, buttonHeight) : undefined}
            aria-keyshortcuts="Control+Alt+M Meta+Alt+M"
            title={t('reportComments.addCommentShortcut')}
            // Pressing the button would otherwise take the selection away.
            onMouseDown={(event) => event.preventDefault()}
            onClick={addComment}>
            <Icon name="AddComment" className={s.Icon} aria-hidden />
            {t('reportComments.addComment')}
        </button>
    );

    if (floats) return button && createPortal(button, document.body);

    return createPortal(
        <SlideUp isOpen={Boolean(button)} className={s.Bar}>
            {button}
        </SlideUp>,
        document.body
    );
};

export default AddCommentButton;
